import { ProgramRole } from "../../API";
import { useEffect, useState } from "react";
import {
  UserReport,
  roundToTwoDecimals,
  useBackendUserReport
} from "./reportUtils";
import { Alert, CircularProgress, Typography } from "@mui/material";
import { format } from "date-fns";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridToolbar
} from "@mui/x-data-grid";
import { ProgramRoleSelectFields } from "../../app/constants";

interface BackendUserReportProps {
  programId?: string;
  columns: GridColDef<UserReport>[];
  handleRowClick: GridEventListener<"rowClick"> | undefined;
  roleFilter: string;
}

export const BackendUserReport = ({
  programId,
  columns,
  handleRowClick,
  roleFilter
}: BackendUserReportProps) => {
  const {
    status: fetchBackendReportStatus,
    error: fetchBackendReportError,
    report: backendReport,
    generatedDate
  } = useBackendUserReport();

  const [report, setReport] = useState<UserReport[]>([]);

  const prepareReport = (programId: string, roleFilter: string | undefined) => {
    const programReport: UserReport[] = [];
    for (const index in backendReport) {
      const item = backendReport[index];
      if (item.program_id === programId && item.user_id) {
        const programRoleValue = item.programrole as ProgramRole;
        const programRoleString = ProgramRoleSelectFields.find(
          (roleSelectField) => roleSelectField.value === item.programrole
        )?.label;
        const reportItem: UserReport = {
          // Creating a few placeholder values to satisfy the Type
          firstName: item.firstname,
          lastName: item.lastname,
          email: item.email || "N/A",
          id: `${index}-${item.user_id}-${item.program_id}`,
          programMembershipId: item.program_membership_id || "N/A",
          userId: item.user_id,
          createdAssessmentEvents: parseInt(item.n_created || "0"),
          assignedAssessmentEvents: parseInt(item.n_assigned || "0"),
          completedAssessmentEvents: parseInt(item.n_complete || "0"),
          percentCompleted: roundToTwoDecimals(
            parseFloat(item.pct_complete || "0.0") * 100
          ),
          expiredAssessmentEvents: parseInt(item.n_expired || "0"),
          percentExpired: roundToTwoDecimals(
            parseFloat(item.pct_expired || "0.0") * 100
          ),
          programRole: programRoleValue,
          programRoleString: programRoleString || "N/A"
        };
        if (roleFilter) {
          if (roleFilter === programRoleValue) {
            programReport.push(reportItem);
          }
        } else {
          programReport.push(reportItem);
        }
      }
    }
    setReport(programReport);
  };

  useEffect(() => {
    if (programId) {
      prepareReport(programId, roleFilter);
    }
  }, [programId, roleFilter]);

  if (fetchBackendReportStatus === "idle") {
    return <div>Awaiting Data...</div>;
  }

  if (fetchBackendReportStatus === "loading") {
    return <CircularProgress />;
  }

  if (fetchBackendReportStatus === "failed") {
    return <Alert severity="error">{fetchBackendReportError}</Alert>;
  }

  return (
    <>
      <div>
        {generatedDate && (
          <Typography>
            {`Data generated: ${format(generatedDate, `PPPPpppp`)}
        `}
          </Typography>
        )}
      </div>
      <DataGrid
        aria-label="reports-table"
        rows={report}
        getRowId={(row) => row.programMembershipId!}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        onRowClick={handleRowClick}
      />
    </>
  );
};
